<template>
  <div class="checkbox">
    <input
      v-bind="$attrs"
      :id="id"
      ref="input"
      :required="required"
      :value="value"
      :checked="isChecked"
      :name="name"
      class="checkbox__field"
      type="checkbox"
      @change="handleChange"
    />

    <label
      v-if="label"
      :for="id"
      class="checkbox__label"
      v-html="label"
    ></label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [Array, String, Number, Boolean],
    default: '',
  },
  value: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: true,
  },
  checked: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const handleChange = e => {
  let value = e.target.checked

  if (Array.isArray(props.modelValue)) {
    ;({ value } = e.target)
    let values

    if (props.modelValue.some(item => item === value)) {
      values = props.modelValue.filter(item => item !== value)
    } else {
      values = props.modelValue
      values.push(value)
    }

    emit('update:modelValue', values)
  } else {
    emit('update:modelValue', value)
  }
}

const isChecked = computed(() => {
  if (props.checked) {
    return props.checked
  }

  if (Array.isArray(props.modelValue)) {
    return props.modelValue.some(item => item === props.value)
  }

  return Boolean(props.modelValue)
})
</script>

<style lang="scss" scoped>
.checkbox,
[class*='checkbox--'] {
  position: relative;
  display: flex;
  min-height: 3.5rem;
}

.checkbox__label {
  margin-top: -0.5rem;
  margin-left: 1rem;
  padding: 0 0 0 4.5rem;
  color: var(--c-foreground);

  :deep(strong) {
    display: block;
  }

  .checkbox & {
    font-size: 1.5rem;
    letter-spacing: 0.5px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3.6rem;
    height: 3.6rem;
    background: var(--c-background);
    border: 0.1rem solid var(--c-foreground);
    border-radius: 3px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0.8rem;
    left: 0.8rem;
    display: none;
    width: 2rem;
    height: 2rem;
    background: var(--c-foreground);
    border-radius: 3px;
  }
}

.checkbox__field {
  @extend %visually-hidden;

  border: 0;
  opacity: 0;

  &:checked + .checkbox__label::after {
    display: block;
  }

  &:disabled + .checkbox__label {
    &::before {
      border-color: var(--c-foreground);
    }

    &::after {
      background-color: var(--c-foreground);
    }
  }
}
</style>
